import React from 'react';
import { Activity, Server, Users, CheckCircle, AlertTriangle, Database } from 'lucide-react';

const SystemHealth = ({ performance = {}, infrastructure = null }) => {
  if (infrastructure?.health) {
    const health = infrastructure.health;
    const queue = infrastructure.queue || {};
    const stripe = infrastructure.stripe_webhooks || {};

    const healthItems = [
      {
        label: 'Database',
        value: health.database || 'unknown',
        status: health.database === 'ok' ? 'good' : health.database === 'fail' ? 'bad' : 'neutral',
        icon: Database,
      },
      {
        label: 'Redis',
        value: health.redis || 'unknown',
        status: health.redis === 'ok' ? 'good' : health.redis === 'fail' ? 'bad' : 'neutral',
        icon: Server,
      },
      {
        label: 'Queue',
        value: `${queue.pending_jobs ?? 0} pending`,
        status: (queue.failed_jobs ?? 0) > 0 ? 'warning' : 'good',
        icon: Activity,
      },
      {
        label: 'Stripe (24h fails)',
        value: stripe.failed_24h ?? 0,
        status: (stripe.failed_24h ?? 0) > 0 ? 'warning' : 'good',
        icon: (stripe.failed_24h ?? 0) > 0 ? AlertTriangle : CheckCircle,
      },
    ];

    return (
      <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">System Health</h3>
        <div className="grid grid-cols-2 gap-4">
          {healthItems.map((item) => {
            const Icon = item.icon;
            const color =
              item.status === 'good'
                ? 'text-emerald-600 dark:text-emerald-400'
                : item.status === 'bad'
                ? 'text-red-600 dark:text-red-400'
                : item.status === 'warning'
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-gray-500 dark:text-gray-400';

            return (
              <div key={item.label} className="p-3 bg-gray-50 dark:bg-gray-800/50 !rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${color}`} />
                  <span className="text-xs text-gray-600 dark:text-gray-400">{item.label}</span>
                </div>
                <p className={`text-lg font-bold capitalize ${color}`}>{item.value}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const systemMetrics = performance?.system_metrics || {};
  const applicationMetrics = performance?.application_metrics || {};
  const userEngagement = performance?.user_engagement || {};

  const uptime = systemMetrics?.uptime?.percentage || systemMetrics?.uptime_percentage || 99.9;
  const responseTime = systemMetrics?.response_time?.average || applicationMetrics?.average_response_time || 150;
  const activeUsers = userEngagement?.active_users || applicationMetrics?.active_users || 0;
  const errorRate = applicationMetrics?.error_rate || applicationMetrics?.api_performance?.error_rate || 0.01;

  const healthItems = [
    {
      label: 'System Uptime',
      value: `${Number(uptime).toFixed(1)}%`,
      status: 'good',
      icon: Server,
    },
    {
      label: 'Response Time',
      value: `${Number(responseTime).toFixed(0)}ms`,
      status: 'good',
      icon: Activity,
    },
    {
      label: 'Active Users',
      value: activeUsers,
      status: 'good',
      icon: Users,
    },
    {
      label: 'Error Rate',
      value: `${Number(errorRate).toFixed(2)}%`,
      status: errorRate > 1 ? 'warning' : 'good',
      icon: errorRate > 1 ? AlertTriangle : CheckCircle,
    },
  ];

  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">System Health</h3>
      <div className="grid grid-cols-2 gap-4">
        {healthItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800/50 !rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Icon
                  className={`w-4 h-4 ${
                    item.status === 'good' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                  }`}
                />
                <span className="text-xs text-gray-600 dark:text-gray-400">{item.label}</span>
              </div>
              <p
                className={`text-lg font-bold ${
                  item.status === 'good' ? 'text-gray-900 dark:text-gray-50' : 'text-amber-600 dark:text-amber-400'
                }`}
              >
                {item.value}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SystemHealth;
